// Mobile Sticky Add to Cart Handler
class MobileStickyATC {
  constructor() {
    this.stickyElement = null;
    this.mainForm = null;
    this.stickyForm = null;
    this.lastScrollTop = 0;
    this.scrollThreshold = 200;
    this.isVisible = false;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Only initialize on mobile devices
    if (window.innerWidth >= 750) return;

    this.stickyElement = document.querySelector('.mobile-sticky-atc');
    if (!this.stickyElement) return;

    this.mainForm = document.querySelector('product-info form[action*="/cart/add"]');
    this.stickyForm = this.stickyElement.querySelector('.mobile-sticky-atc__form');
    
    if (!this.mainForm || !this.stickyForm) return;

    this.bindScrollListener();
    this.bindFormSync();
    this.bindVariantChanges();
    this.setupQuantitySync();
  }

  bindScrollListener() {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldShow = scrollTop > this.scrollThreshold;

    if (shouldShow && !this.isVisible) {
      this.showSticky();
    } else if (!shouldShow && this.isVisible) {
      this.hideSticky();
    }

    this.lastScrollTop = scrollTop;
  }

  showSticky() {
    this.isVisible = true;
    this.stickyElement.classList.add('is-visible');
  }

  hideSticky() {
    this.isVisible = false;
    this.stickyElement.classList.remove('is-visible');
  }

  bindFormSync() {
    // Sync main form data to sticky form
    this.syncFormData();

    // Handle sticky form submission
    this.stickyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleStickySubmit();
    });
  }

  syncFormData() {
    const mainVariantInput = this.mainForm.querySelector('input[name="id"]');
    const stickyVariantInput = this.stickyForm.querySelector('input[name="id"]');
    
    if (mainVariantInput && stickyVariantInput) {
      stickyVariantInput.value = mainVariantInput.value;
    }

    const mainQtyInput = this.mainForm.querySelector('input[name="quantity"]');
    const stickyQtyInput = this.stickyForm.querySelector('input[name="quantity"]');
    
    if (mainQtyInput && stickyQtyInput) {
      stickyQtyInput.value = mainQtyInput.value || 1;
    }
  }

  bindVariantChanges() {
    // Listen for variant changes from main product form
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          this.syncFormData();
        }
      });
    });

    const mainVariantInput = this.mainForm.querySelector('input[name="id"]');
    if (mainVariantInput) {
      observer.observe(mainVariantInput, { attributes: true });
      
      // Also listen for change events
      mainVariantInput.addEventListener('change', () => {
        this.syncFormData();
      });
    }

    // Listen for pubsub variant changes if available
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.variant) {
          this.updateStickyForVariant(data.variant);
        }
      });
    }
  }

  setupQuantitySync() {
    const mainQtyInput = this.mainForm.querySelector('input[name="quantity"]');
    const stickyQtyInput = this.stickyForm.querySelector('input[name="quantity"]');
    
    if (!mainQtyInput || !stickyQtyInput) return;

    // Sync main to sticky
    mainQtyInput.addEventListener('change', () => {
      stickyQtyInput.value = mainQtyInput.value;
    });

    mainQtyInput.addEventListener('input', () => {
      stickyQtyInput.value = mainQtyInput.value;
    });

    // Sync sticky to main
    stickyQtyInput.addEventListener('change', () => {
      mainQtyInput.value = stickyQtyInput.value;
      mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    stickyQtyInput.addEventListener('input', () => {
      mainQtyInput.value = stickyQtyInput.value;
      mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Handle quantity buttons
    this.setupQuantityButtons();
  }

  setupQuantityButtons() {
    const quantityComponent = this.stickyForm.querySelector('quantity-input');
    if (!quantityComponent) return;

    const minusBtn = quantityComponent.querySelector('.quantity__button--minus');
    const plusBtn = quantityComponent.querySelector('.quantity__button--plus');
    const input = quantityComponent.querySelector('.quantity__input');

    if (minusBtn) {
      minusBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        const minValue = parseInt(input.getAttribute('min')) || 1;
        const newValue = Math.max(minValue, currentValue - 1);
        input.value = newValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        const maxValue = parseInt(input.getAttribute('max'));
        const newValue = maxValue ? Math.min(maxValue, currentValue + 1) : currentValue + 1;
        input.value = newValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  updateStickyForVariant(variant) {
    const stickyVariantInput = this.stickyForm.querySelector('input[name="id"]');
    if (stickyVariantInput) {
      stickyVariantInput.value = variant.id;
    }

    // Update button state based on availability
    const submitBtn = this.stickyForm.querySelector('.mobile-sticky-atc__button');
    if (submitBtn) {
      if (variant.available) {
        submitBtn.disabled = false;
        submitBtn.querySelector('span').textContent = submitBtn.dataset.addText || 'Add to Bag';
      } else {
        submitBtn.disabled = true;
        submitBtn.querySelector('span').textContent = submitBtn.dataset.soldOutText || 'Sold Out';
      }
    }
  }

  handleStickySubmit() {
    const submitBtn = this.stickyForm.querySelector('.mobile-sticky-atc__button');
    if (submitBtn.disabled) return;

    // Show loading state
    submitBtn.classList.add('loading');
    submitBtn.disabled = true;

    // Submit the form data
    const formData = new FormData(this.stickyForm);
    
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
    .then(response => response.json())
    .then(data => {
      // Success - trigger cart update events
      this.handleAddToCartSuccess(data);
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      this.handleAddToCartError(error);
    })
    .finally(() => {
      // Remove loading state
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    });
  }

  handleAddToCartSuccess(data) {
    // Trigger cart update events
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, undefined);
    }

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('cart:item-added', {
      detail: { item: data }
    }));

    // Show success feedback (could be a toast, cart drawer, etc.)
    this.showAddToCartFeedback();
  }

  handleAddToCartError(error) {
    // Show error feedback
    console.error('Failed to add to cart:', error);
    // Could show a toast or alert here
  }

  showAddToCartFeedback() {
    // Could trigger cart drawer or show success message
    // For now, just log success
    console.log('Product added to cart successfully');
  }
}

// Initialize when script loads
document.addEventListener('DOMContentLoaded', () => {
  new MobileStickyATC();
});
